import { FC } from "react";
import { NOTIFICATION_TIMELINE } from "../types/CommonTypes";
import formatDateTime from "../../../helpers/formatDateTime";
import { Box, Typography } from "@mui/material";
import { globalStyles } from "../../../styles/GlobalStyles";
import Icons from "../../../assets/Icons";
import { useTranslation } from "react-i18next";
import NoteChip from "./NoteChip";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";

interface NotificationNoteTimelineProps {
  item: NOTIFICATION_TIMELINE;
  hideLine?: boolean;
  isNotLast?: boolean;
}

const NotificationNoteTimeline: FC<NotificationNoteTimelineProps> = ({
  item,
  hideLine = false,
  isNotLast = false,
}) => {
  const { sendTo, message, loggedBy, createdAt }: NOTIFICATION_TIMELINE = item;
  const { t } = useTranslation();

  return (
    <Box sx={globalStyles.row}>
      {!hideLine && (
        <Box sx={styles.timelineColumn}>
          <Box
            border={1}
            borderRadius={3}
            borderColor="custom.border.default"
            width={18}
            height={18}
            m={0.25}
            style={globalStyles.center}
          >
            <Icons.SmsFilled width={12} height={12} />
          </Box>
          {isNotLast && <Box sx={styles.line} />}
        </Box>
      )}

      <Box
        sx={[
          styles.card,
          !hideLine && isNotLast && styles.cardSpacing,
          hideLine && styles.compactCard,
        ]}
      >
        <Box sx={styles.headerRow}>
          <Typography sx={styles.title}>{t("SMS")}</Typography>
        </Box>

        <Typography sx={styles.description}>{message}</Typography>

        <Typography sx={styles.sendToText}>
          {t("Send_To", {
            send_to: sendTo,
            interpolation: { escapeValue: false },
          })}
        </Typography>

        <Typography sx={styles.metaText}>
          {t("SMS_sent_at_and_by", {
            sent_at: formatDateTime(createdAt, true, "hh:mm:ss aaa"),
            sent_by: loggedBy,
          })}
        </Typography>

        <Box sx={styles.chipContainer}>
          <NoteChip
            icon={<ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
            label={t("SMS")}
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  timelineColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mr: "12px",
  },

  line: {
    width: "4px",
    flex: 1,
    borderRadius: "4px",
    backgroundColor: "custom.background.activity",
  },

  card: {
    flex: 1,
    minWidth: 0,
    minHeight: "88px",

    backgroundColor: "custom.background.primary",

    border: "1px solid #E5E7EB",
    borderRadius: "8px",

    px: "12px",
    py: "10px",

    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  cardSpacing: {
    mb: "16px",
  },

  compactCard: {
    width: "100%",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",

    columnGap: "6px",
    rowGap: "4px",

    width: "100%",
  },

  title: {
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,

    color: "custom.foreground.primary",
  },

  description: {
    mt: "3px",

    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: 400,

    color: "custom.foreground.primary",

    width: "100%",
    maxWidth: "100%",

    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  sendToText: {
    mt: "3px",

    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: 700,

    color: "custom.foreground.primary",
  },

  metaText: {
    mt: "3px",

    fontSize: "12px",
    lineHeight: "16px",

    color: "custom.foreground.secondary",
  },

  chipContainer: {
    mt: "6px",

    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
};

export default NotificationNoteTimeline;
